import {z} from "zod";
import countries from "world-countries";
import parsePhoneNumberFromString, {CountryCode} from "libphonenumber-js";

export {zHttpUrl} from "@docid/api/src/utils/validation";

/**
 * Regex validators
 */
export const postalCodeRegex = /^[A-Z0-9\s\-]{3,10}$/i; // This can be adapted for specific countries if needed
export const cityRegex = /^[\p{L}\s'-]+$/u; // Allows letters, spaces, and hyphens for city names
export const vatRegex = /^[A-Z]{2}[A-Z0-9]{8,12}$/; // A basic regex for EU VAT numbers, with country code prefix

export const zHttpUrlOptional = (errorMessage?: string) => z
    .string()
    .trim()
    .refine(value => {
        if (value === "") return true;
        return /^https?:\/\//i.test(value) && z.string().url().safeParse(value).success;
    }, {message: errorMessage});

/**
 * Validate uuid
 * @param id
 */
export const validateUUID = (id: any): string => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? (id as string) : "";
};

/**
 * Validates that a wysiwyg field is not empty
 */
export function wysiwygNotEmpty(min = 1) {
    return (val: string) => {
        const plainText: string = val.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        return plainText.length >= min;
    };
}

/**
 * Validate and transform phone numbers
 *
 * country CountryCode
 * errorMessage string
 */
export const zPhone = (country: any, errorMessage?: string) => z.string().trim()
    .min(1, { message: errorMessage || "Phone number is required" })
    .transform((arg, ctx) => {

        const phone = parsePhoneNumberFromString(arg, {
            defaultCountry: country as CountryCode,
            extract: false,
        });

        if (phone?.isValid()) {
            return phone.format("E.164") as string;
        }

        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage || "Invalid phone number",
        });

        return z.NEVER;

    }
);

/**
 * Validate and transform optional phone numbers
 *
 * country CountryCode
 * errorMessage string
 */
export const zPhoneOptional = (country: any, errorMessage: string) => z.string().trim().transform((value, ctx) => {

    if (value === "") {
        return "";
    }

    const phone = parsePhoneNumberFromString(value, {
        defaultCountry: country,
        extract: false,
    });

    if (phone?.isValid()) {
        return phone.format("E.164") as string;
    }

    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage || "Invalid phone number",
    });

    return z.NEVER;

});

const validCountryCodes = countries.map(country => country.cca2);

export const zCountry = (errorMessage?: string) => z.string()
    .trim()
    .refine(value => validCountryCodes.includes(value.toUpperCase()), {
        message: errorMessage || "Invalid country",
    });

export const zCountryOptional = (errorMessage?: string) => z.string()
    .trim()
    .transform((value, ctx) => {
        if (value === "") return "";

        const upperValue = value.toUpperCase();
        if (validCountryCodes.includes(upperValue)) {
            return upperValue;
        }

        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage || "Invalid country code",
        });

        return z.NEVER;
    });
